import { Box, Button, Count, Icon } from '@vp/swan';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { CaseDataContext } from '../../contexts/CaseDataContext';
import { CAContext } from '../../contexts/CAContext';
import { chatButtonConflictingPosition } from '../../config/miaw/button-position';
import { t } from '../../utils/translations';
import './Chatbutton.styles.scss';
import './ChatButtonPositions.styles.scss';
import { fetchChatButtonConfig, getButtonConfig } from '../../utils/chat/chat-button-utils';
import { handleProductPages } from '../../utils/chat/product-page-utils';
import { handleFilters } from '../../utils/chat/discovery-page';
import { fireImpression, getVariation } from '@vp/ab-reader';
import { isHelpCenterPage } from '../../utils/chat/page-utils';

interface IChatButtonProps {
  onClick?: () => void;
}

const ChatButton: React.FC<IChatButtonProps> = ({ onClick }) => {
  const [customButtonConfig, setCustomButtonConfig] = useState<{ chatbotEnabledPages: string[] }>();
  const [isConfigLoaded, setIsConfigLoaded] = useState<boolean>(false);
  const [showChatButton, setShowChatButton] = useState(false);
  const [conflictingButtonPosition, setConflictingButtonPosition] = useState<string>('');

  const {
    chatState: { unreadMessages, enableChatbot, chatButtonText, chatSession, chatWindow, locale },
    setChatState,
  } = useContext(CAContext);

  const {
    state: { pageInfo },
  } = useContext(CaseDataContext);

  useEffect(() => {
    const fetchData = async () => {
      return await fetchChatButtonConfig();
    };
    fetchData()
      .then((res) => {
        setCustomButtonConfig(res ? res[locale] : undefined);
      })
      .finally(() => {
        setIsConfigLoaded(true);
      });
  }, []);

  const getFormattedUnreadMessageCount = () => {
    if (unreadMessages) {
      if (unreadMessages > 10) {
        return '10+';
      } else {
        return unreadMessages.toString();
      }
    }
    return undefined;
  };

  useEffect(() => {
    if (isConfigLoaded) {
      const { enableChat } = getButtonConfig(customButtonConfig);
      if (enableChat || chatSession != 'initial') setShowChatButton(chatWindow === 'minimized');
    }
  }, [chatWindow, chatSession, customButtonConfig, isConfigLoaded]);

  useEffect(() => {
    if (isConfigLoaded) {
      const { enableChat, buttonType } = getButtonConfig(customButtonConfig);
      if (
        enableChat && // IF chat is enabled
        buttonType === 'inline' &&
        (chatSession === 'initial' || chatSession === 'endChatSession')
      ) {
        setShowChatButton(false);
      }
    }
  }, [chatSession, customButtonConfig, isConfigLoaded]);

  const chatButtonHandler = useCallback((action: 'show' | 'hide') => {
    if (action === 'show') {
      setShowChatButton(true);
    } else if (action === 'hide') {
      setShowChatButton(false);
    }
  }, []);

  useEffect(() => {
    const pageURL = window.location.href;
    chatButtonConflictingPosition.forEach((item) => {
      if (pageURL.includes(t(item.pageName))) {
        setConflictingButtonPosition(item.className);
      }
    });
    /** To Hide chat button when filters are opened */
    handleFilters();

    /** To Change the position of the chat button for product pages */
    setTimeout(() => {
      handleProductPages(pageInfo?.pageName || '');
    }, 5000);
  }, []);

  useEffect(() => {
    setChatState((prevState) => ({
      ...prevState,
      chatButtonHandler: chatButtonHandler,
    }));
  }, [chatButtonHandler]);

  useEffect(() => {
    if (isHelpCenterPage()) {
      const variationKey = getVariation('va_experiment_helpcenter_us');
      if (locale === 'en-us' && variationKey === 'test') {
        fireImpression('va_experiment_helpcenter_us', variationKey);
      }
    }
  }, []);

  if (showChatButton === false) {
    return null;
  }
  return (
    <Button
      className={`caHelpButton ${conflictingButtonPosition}`} // append conflicting class if exists
      iconPosition="left"
      compactMode
      onClick={onClick}
      id={`caHelpButton`}
    >
      {unreadMessages
        ? unreadMessages > 0 && (
            <Count
              style={{
                top: '-8px',
                position: 'absolute',
                right: '-11px',
              }}
              skin="alert"
            >
              {getFormattedUnreadMessageCount() || ''}
            </Count>
          )
        : null}
      {chatSession === 'miawChatSession' ? <Box className="chatButtonActiveIcon"></Box> : null}
      <Icon iconType={enableChatbot || chatSession === 'chatbotSession' ? 'aiSupport' : 'liveChat'} />
      {chatButtonText}
    </Button>
  );
};

export default ChatButton;
